import { useMemo } from "react";
import { useAtomValue } from "jotai";
import { useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import {
  extractDayFromDate,
  formatDeliveryTimeRange,
  formatTimeAMPM,
  shouldShowTimeRange
} from "common/utils/format/date";
import { fetchPdpDeliveryEstimates } from "common/utils/delivery-date/fetch-pdp-delivery-estimates";
import { transfersManagerAtom } from "./transfers-manager.machine";

const DEFAULT_CATALOG_NUMBER = "DTF001";
const DTF_COMPOSITION_NAME = "Transfer";

type DtfPrintItem = {
  artwork_id: number;
  width: number;
  height: number;
  quantity: number;
};

type DtfPrintAccumulator = {
  dtfPrint: DtfPrintItem[];
  keys: string[];
};

export function useTransfersLabDeliveryEstimates() {
  const [searchParams] = useSearchParams();
  const transfersManager = useAtomValue(transfersManagerAtom);

  const { items } = transfersManager.context;

  const productSlug = useMemo(() => {
    const catalogNumber = searchParams.get("catalog_number") || DEFAULT_CATALOG_NUMBER;
    return `jiffytransfers-${catalogNumber}`;
  }, [searchParams]);

  const { dtfPrint, itemsKey } = useMemo(() => {
    const result = items.reduce<DtfPrintAccumulator>(
      (acc, artworkActor) => {
        const { artwork, quantity, sizesFormControl } = artworkActor.getSnapshot().context;

        if (!artwork || quantity <= 0) {
          return acc;
        }

        let width = artwork.metadata.widthInch;
        let height = artwork.metadata.heightInch;

        if (sizesFormControl) {
          const { artworkOrder, artworks } = sizesFormControl.getValues();
          if (artworkOrder && artworkOrder.length > 0) {
            const firstArtworkId = artworkOrder[0];
            if (firstArtworkId) {
              const artworkData = artworks[firstArtworkId];
              const standardTransfer = artworkData?.transferTypes?.["standard"];
              if (standardTransfer?.customPreset?.customSizes) {
                const { customSizes } = standardTransfer.customPreset;
                const firstSize = customSizes[0];
                if (firstSize) {
                  width = firstSize.width;
                  height = firstSize.height;
                }
              }
            }
          }
        }

        acc.dtfPrint.push({
          artwork_id: artwork.id,
          width,
          height,
          quantity
        });

        acc.keys.push(`${artwork.id}-${width}-${height}-${quantity}`);

        return acc;
      },
      {
        dtfPrint: [],
        keys: []
      }
    );

    return {
      dtfPrint: result.dtfPrint,
      itemsKey: result.keys.join("|")
    };
  }, [items]);

  const {
    data: deliveryData,
    isLoading,
    isError
  } = useQuery({
    queryKey: ["transfersLabDeliveryEstimates", productSlug, itemsKey],
    queryFn: async () => {
      const payload = {
        delivery_estimate: {
          composition_name: DTF_COMPOSITION_NAME,
          variants_hash: JSON.stringify({}),
          pre_cut: false,
          dtf_print: dtfPrint
        }
      };

      try {
        const response = await fetchPdpDeliveryEstimates({ productId: productSlug }, payload);

        return response;
      } catch (error) {
        window.Honeybadger?.notify("Error fetching delivery estimates on Transfers Lab", {
          context: {
            error,
            productSlug,
            itemsKey,
            dtfPrintCount: dtfPrint.length
          }
        });
        throw error;
      }
    }
  });

  const compositionDates = deliveryData?.composition_dates;
  const valueFull = compositionDates?.value_full;
  const deliveryTime = compositionDates?.delivery_time;
  const deliveryTimeEnd = compositionDates?.delivery_time_end;
  const timeLeft = compositionDates?.timeleft;

  const isTimeRangeAvailable =
    valueFull && shouldShowTimeRange(valueFull) && deliveryTime && deliveryTimeEnd;

  const timeRange = isTimeRangeAvailable
    ? `${extractDayFromDate(valueFull)} ${formatDeliveryTimeRange(deliveryTime, deliveryTimeEnd)}`
    : undefined;

  const formattedTimeLeft = timeLeft ? formatTimeAMPM(timeLeft.toString(), false) : undefined;

  return {
    valueFull,
    timeRange,
    formattedTimeLeft,
    isLoading,
    isError,
    isOutOfStock: false
  };
}
